import { useState, useEffect, useCallback, useRef } from 'react';
import { Minus, Plus, Loader2, PackageCheck, History, RotateCcw } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import OrderStatusBadge from '@/components/OrderStatusBadge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PRODUCTS } from '@/data/products';
import { useAuth } from '@/context/AuthContext';
import { api, formatApiError } from '@/lib/api';

const fmtDate = (iso) => new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });

export default function CustomerPortal() {
  const { user } = useAuth();
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const builderRef = useRef(null);

  const reorder = (order) => {
    const known = new Set(PRODUCTS.map((p) => p.slug));
    const nextQty = {};
    order.items.forEach((i) => { if (known.has(i.slug)) nextQty[i.slug] = i.cases; });
    setQty(nextQty);
    setNotes(order.notes || '');
    setAddress(order.delivery_address || '');
    toast.success(`Order #${order.number} loaded`, { description: 'Adjust the quantities, then submit when ready.' });
    builderRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/orders');
      setOrders(data.orders);
    } catch {
      setOrders([]);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const setCases = (slug, n) => setQty((q) => ({ ...q, [slug]: Math.max(0, Math.min(999, n)) }));
  const totalCases = Object.values(qty).reduce((a, b) => a + b, 0);

  const submitOrder = async () => {
    const items = PRODUCTS.filter((p) => qty[p.slug] > 0).map((p) => ({ slug: p.slug, name: p.name, cases: qty[p.slug] }));
    if (items.length === 0) return toast.error('Add at least one case to your order.');
    setBusy(true);
    try {
      const { data } = await api.post('/api/orders', { items, notes: notes || null, delivery_address: address || null });
      toast.success(`Order #${data.order.number} received`, { description: 'A confirmation email is on its way.' });
      setQty({}); setNotes(''); setAddress('');
      loadOrders();
    } catch (err) {
      toast.error('Could not place order', { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-28 md:pt-36 pb-24 container mx-auto" data-testid="customer-portal">
        <div className="font-script text-2xl text-leaf">wholesale portal</div>
        <h1 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
          Welcome back, {user?.name?.split(' ')[0]}.
        </h1>
        <p className="text-muted-foreground mt-2">{user?.org} · Ordering by the case — our team confirms pricing on every order.</p>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          {/* Order builder */}
          <div className="lg:col-span-7" ref={builderRef}>
            <h2 className="font-display text-2xl font-semibold text-primary flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-leaf" /> Place an order
            </h2>
            <div className="mt-5 grid gap-3">
              {PRODUCTS.map((p) => (
                <div key={p.slug} className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border/70" data-testid={`order-product-${p.slug}`}>
                  <img src={p.image} alt={p.name} className="h-14 w-14 rounded-xl object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-primary leading-tight truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Case: {p.unitCase}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => setCases(p.slug, (qty[p.slug] || 0) - 1)} data-testid={`order-minus-${p.slug}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <span className="w-8 text-center font-display font-semibold text-primary" data-testid={`order-qty-${p.slug}`}>{qty[p.slug] || 0}</span>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => setCases(p.slug, (qty[p.slug] || 0) + 1)} data-testid={`order-plus-${p.slug}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="order-address" className="text-primary mb-1.5">Delivery address (optional)</Label>
                <Input id="order-address" value={address} onChange={(e) => setAddress(e.target.value)}
                  placeholder="Dock 4, 200 College St, Toronto, ON" className="h-11 rounded-xl" data-testid="order-address-input" />
              </div>
              <div>
                <Label htmlFor="order-notes" className="text-primary mb-1.5">Notes (optional)</Label>
                <Textarea id="order-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Delivery window, PO number, special instructions…" className="rounded-xl" data-testid="order-notes-input" />
              </div>
              <Button onClick={submitOrder} disabled={busy || totalCases === 0}
                className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-base" data-testid="order-submit-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Submit order${totalCases ? ` · ${totalCases} case${totalCases > 1 ? 's' : ''}` : ''}`}
              </Button>
            </div>
          </div>

          {/* Order history */}
          <div className="lg:col-span-5">
            <h2 className="font-display text-2xl font-semibold text-primary flex items-center gap-2">
              <History className="h-5 w-5 text-leaf" /> Your orders
            </h2>
            <div className="mt-5 grid gap-3" data-testid="order-history">
              {orders === null ? (
                <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-leaf" /></div>
              ) : orders.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No orders yet — your first order will appear here.
                </div>
              ) : orders.map((o) => (
                <div key={o.id} className="p-5 rounded-2xl bg-card border border-border/70" data-testid={`order-card-${o.number}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-display font-semibold text-primary">Order #{o.number}</div>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtDate(o.created_at)}</div>
                  <ul className="mt-3 space-y-1">
                    {o.items.map((i) => (
                      <li key={i.slug} className="text-sm text-muted-foreground flex justify-between gap-3">
                        <span className="truncate">{i.name}</span>
                        <span className="font-medium text-primary shrink-0">{i.cases} case{i.cases > 1 ? 's' : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {o.notes && <p className="text-xs text-muted-foreground mt-3 italic">"{o.notes}"</p>}
                  <Button variant="outline" size="sm" className="rounded-full mt-4 w-full"
                    onClick={() => reorder(o)} data-testid={`reorder-button-${o.number}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reorder — load & edit
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
